import {
  HOUSE_ABOUT_STEP,
  HOUSE_FIND_STEP,
  HOUSE_PRICE_STEP,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/House";
import {
  booleanToFormBoolean,
  nullableBooleanToNullableFormBoolean,
} from "Utils/formUtils";

import { RentalOrUnoccupied } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/House/houseTypes";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { FormStates } from "Store/priceCalculation/priceCalculationInterfaces";
import { mapInjuriesToForm } from "Store/priceCalculation/productMappers/common/injuriesMapper";
import { assertExhaustive, isDefined } from "Utils/functions";
import { createKey } from "Utils/generalUtils";
import { DEDUCTIBLE_FOUR_THOUSAND } from "Utils/priceUtils";
import { AdditionalBuildingType } from "../../AdditionalBuildings/additionalBuildingsInterfaces";
import { DEFAULT_DEDUCTIBLE_HOUSE } from "../houseConstants";
import {
  AdditionalBuildingFormType,
  AdditionalBuildingFormValues,
  HousePriceFormValues,
  HouseProductDetailsFormValues,
  HouseProductInfoDetailsFormValues,
  HouseProductIntroFormValues,
} from "../houseFormValuesInterfaces";
import { HOUSE_BASIC_INFO_STEP } from "../houseSteps";

export function mapFromCommerceHouse(data: { [key: string]: any }): FormStates {
  const deductibleCode = String(data.priceSpecifications.deductible?.code);

  const findStep: HouseProductIntroFormValues = {
    addressSameAsHomeAddress: nullableBooleanToNullableFormBoolean(
      data.addressSameAsHomeAddress,
    ),
    recentlyBought: nullableBooleanToNullableFormBoolean(data.recentlyBought),
    postNumber: data.address?.zipCode,
    city: data.address?.city,
    address: data.address?.line1,
    cadastralGnr: isDefined(data.address?.cadastralGnr)
      ? Number(data.address.cadastralGnr)
      : undefined,
    cadastralBnr: isDefined(data.address?.cadastralBnr)
      ? Number(data.address.cadastralBnr)
      : undefined,
    roofType: data.roofType,
    hasBasementRoom: data.hasBasementRoom,
    hasFloodRisk: data.hasFloodRisk,
    realEstateInfo: data.realEstateInfo,
  };

  const basicInfoStep: HouseProductInfoDetailsFormValues = {
    constructionYear: String(data.constructionYear),
    bta: data.grossArea,
    boa: data.livingFloorSpace,
    houseType: data.houseType,
    otherBuildings: mapOtherBuildings(data.additionalBuildings),
    saved: true,
  };

  const aboutStep: HouseProductDetailsFormValues = {
    historicalValue: BooleanFormType.FALSE,
    hasBasementRoom: nullableBooleanToNullableFormBoolean(data.hasBasementRoom),
    rentalOrUnoccupied: mapRentalOrUnoccupied(
      data.hasRental,
      data.hasPartialRental,
    ),
    validNumberOfRentalUnits: BooleanFormType.TRUE,
    validNumberOfInhabitants: BooleanFormType.TRUE,
    isRoofChanged: nullableBooleanToNullableFormBoolean(data.isRoofChanged),
    isWetAreasRehabilitatedOrChanged: nullableBooleanToNullableFormBoolean(
      data.isWetAreasRehabilitatedOrChanged,
    ),
    isWithHistoryOfWaterOrMoistureDamage: nullableBooleanToNullableFormBoolean(
      data.isWithHistoryOfOfWaterOrMoistureDamage,
    ),
    pestsDamaged: nullableBooleanToNullableFormBoolean(
      data.previousFungusDamage,
    ),
    ...mapInjuriesToForm(data.damageCountRecentYears, data.yearOfLastDamage),
    maintenanceConfirmation: booleanToFormBoolean(data.maintenanceConfirmation),
    hasFireAlarm: data.hasFireAlarm ?? false,
    hasBurglarAlarm: data.hasBurglarAlarm ?? false,
    hasWaterGuard: data.hasWaterGuard ?? false,
    hasCircuitBreakers: data.hasCircuitBreakers ?? false,
    hasPipeInPipeSystem: data.hasPipeInPipeSystem ?? false,
  };

  const priceStep: HousePriceFormValues = {
    deductible:
      deductibleCode === DEDUCTIBLE_FOUR_THOUSAND
        ? DEFAULT_DEDUCTIBLE_HOUSE
        : deductibleCode,
    coverage: data.coverageGroup,
    buyHouseContentsInsurance: BooleanFormType.FALSE,
    onlyFireAndNatureDamage: data.onlyFireAndNatureDamage ?? false,
  };

  return {
    [HOUSE_FIND_STEP.id]: findStep,
    [HOUSE_BASIC_INFO_STEP.id]: basicInfoStep,
    [HOUSE_ABOUT_STEP.id]: aboutStep,
    [HOUSE_PRICE_STEP.id]: priceStep,
  };
}

const mapOtherBuildings = (buildings: any[]): AdditionalBuildingFormValues[] =>
  buildings?.map((building) => ({
    id: createKey(),
    type: mapToAdditionalBuildingFormType(building),
    areal: building.grossArea,
    constructionYear: String(building.constructionYear),
    saved: true,
  })) ?? [];

const mapToAdditionalBuildingFormType = (building: {
  type: AdditionalBuildingType;
  isIsolated: boolean;
}): AdditionalBuildingFormType => {
  switch (building.type) {
    case AdditionalBuildingType.CARPORT:
      return AdditionalBuildingFormType.CARPORT;
    case AdditionalBuildingType.GARAGE:
      return building.isIsolated
        ? AdditionalBuildingFormType.GARAGE_ISOLATED
        : AdditionalBuildingFormType.GARAGE_NON_ISOLATED;
    case AdditionalBuildingType.OUTHOUSE:
      return building.isIsolated
        ? AdditionalBuildingFormType.OUTHOUSE_ISOLATED
        : AdditionalBuildingFormType.OUTHOUSE_NON_ISOLATED;
    case AdditionalBuildingType.GARAGE_OUTHOUSE_ANNEX:
      return AdditionalBuildingFormType.GARAGE_OUTHOUSE_ANNEX;
    case AdditionalBuildingType.ANNEX:
      throw new Error(
        `Invalid additional building type for house: ${building.type}`,
      );
    default:
      return assertExhaustive(
        building.type,
        new Error(`Unmapped FMS additional building type: ${building.type}.`),
      );
  }
};

const mapRentalOrUnoccupied = (
  hasRental: boolean,
  hasPartialRental: boolean,
): RentalOrUnoccupied => {
  if (hasRental) {
    return RentalOrUnoccupied.FULL_RENTAL;
  } else if (hasPartialRental) {
    return RentalOrUnoccupied.PARTIAL_RENTAL;
  }
  return RentalOrUnoccupied.NO_RENTAL;
};
